/** PersonaResultCard — the /profile command's "I AM A …" archetype card. */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { PersonaCard } from '../PersonaCard';
import type { PersonaInputs } from '../PersonaCard';
import type { TripRow } from '../TravelReport';
import type { ReservationRow } from '../ReservationsReport';
import { classifyTrips } from '../onboarding-plans';

interface DiscoveryStatusSlim {
  jobs?: {
    calendar?: { travelTrips?: TripRow[]; restaurantReservations?: ReservationRow[] };
    inbox?: { restaurantReservations?: ReservationRow[] };
    sent?: { sentTopics?: Array<{ topic: string; count: number }>; innerCircleSize?: number; contactCount?: number; circle?: { regulars?: number } };
  };
}

export function PersonaResultCard() {
  const [inputs, setInputs] = useState<PersonaInputs | null | undefined>(undefined);
  const { token } = useSession();

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const wsId = getActiveWorkspaceId();
        const data = await apiFetch<DiscoveryStatusSlim>(
          `/workspaces/${wsId}/onboarding/discovery-status`,
          { authToken: token },
        );
        const allTrips = data?.jobs?.calendar?.travelTrips ?? [];
        const { recent, upcoming } = classifyTrips(allTrips);
        const trips: TripRow[] = [...recent, ...upcoming.map((t) => ({ ...t, planned: true }))];
        const calRes = data?.jobs?.calendar?.restaurantReservations ?? [];
        const mailRes = data?.jobs?.inbox?.restaurantReservations ?? [];
        const calKeys = new Set(calRes.map((r) => `${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`));
        const reservations = [
          ...calRes,
          ...mailRes.filter((r) => !calKeys.has(`${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`)),
        ];
        if (alive) {
          setInputs({
            trips,
            reservations,
            lodgings: allTrips.map((t) => t.lodging).filter(Boolean) as string[],
            sentTopics: data?.jobs?.sent?.sentTopics ?? [],
            innerCircleSize: data?.jobs?.sent?.innerCircleSize,
            // circle.regulars (rollup-derived) beats the sample contactCount
            contactCount: data?.jobs?.sent?.circle?.regulars ?? data?.jobs?.sent?.contactCount,
          });
        }
      } catch {
        if (alive) setInputs(null);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (inputs === undefined) return null;
  if (!inputs || (inputs.trips.length === 0 && inputs.reservations.length === 0)) {
    return <p>Not enough data for your card yet — connect your accounts and let a sync finish.</p>;
  }
  return <PersonaCard inputs={inputs} />;
}
